import { AstroPayI18n } from '@core/i18n';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

import { convertData } from '@utils/object';

import { mockPath } from './mockpath';
import {
  TAstroPayHttpError,
  TAstroPayHttpSetting,
  TMethodDELETE,
  TMethodGET,
  TMethodPATCH,
  TMethodPOST,
  TMethodPUT,
} from './types';

export class HttpClient {
  private static axiosInstance: AxiosInstance;
  private static config: TAstroPayHttpSetting;
  private static appDataRequest?: string;

  public static setAppDataRequest(appIdToken: string): void {
    this.appDataRequest = appIdToken;
  }

  public static init = (config: TAstroPayHttpSetting): void => {
    this.config = config;
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout ?? 5_000,
      withCredentials: true, // false when using mockserver
      headers: {
        'Accept-Language': AstroPayI18n.getCurrent(),
        ...config.headers,
      },
    });

    this.setupInterceptors();
  };

  // #region API Methods
  public static GET = async <T>({
    url,
    params = {},
    mock = false,
    useQueryParams = true,
  }: TMethodGET): Promise<T> => {
    const endpoint = mockPath(url, mock);

    const config = {
      params: {
        useQueryParams,
        ...params,
      },
    };

    const response = (await this.getAxiosInstance().get<T>(endpoint, config)) as T;
    return response;
  };

  public static POST = async <T>({
    url,
    data = {},
    mock = false,
    useQueryParams = true,
  }: TMethodPOST): Promise<T> => {
    const endpoint = mockPath(url, mock);

    const config = {
      params: {
        useQueryParams,
      },
      headers: {
        'Content-Type': 'text/plain',
      },
    };

    const response = (await this.getAxiosInstance().post<T>(endpoint, data, config)) as T;
    return response;
  };

  public static PUT = async <T>({
    url,
    data = {},
    mock = false,
    useQueryParams = true,
  }: TMethodPUT): Promise<T> => {
    const endpoint = mockPath(url, mock);

    const config = {
      params: {
        useQueryParams,
      },
    };

    const response = (await this.getAxiosInstance().put<T>(endpoint, data, config)) as T;
    return response;
  };

  public static PATCH = async <T>({
    url,
    data = {},
    mock = false,
    useQueryParams = true,
  }: TMethodPATCH): Promise<T> => {
    const endpoint = mockPath(url, mock);

    const config = {
      params: {
        useQueryParams,
      },
      headers: {
        'Content-Type': 'text/plain',
      },
    };

    const response = (await this.getAxiosInstance().patch<T>(endpoint, data, config)) as T;
    return response;
  };

  public static DELETE = async <T>({
    url,
    params = {},
    mock = false,
    useQueryParams = true,
  }: TMethodDELETE): Promise<T> => {
    const endpoint = mockPath(url, mock);

    const config = {
      params: {
        useQueryParams,
        ...params,
      },
    };

    const response = (await this.getAxiosInstance().delete<T>(endpoint, config)) as T;
    return response;
  };
  // #endregion

  // #region Useful methods
  private static getAxiosInstance(): AxiosInstance {
    if (!this.axiosInstance) {
      throw new Error('Axios instance not initialized');
    }
    return this.axiosInstance;
  }

  private static setupInterceptors = (): void => {
    this.axiosInstance.interceptors.request.use((config) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { useQueryParams, ...cleanParams } = (config.params || {}) as Record<string, any>;

      if (useQueryParams) {
        config.params = {
          ...cleanParams,
          ...(this.appDataRequest ? { data: this.appDataRequest } : {}), // optional DATA request
        };
      } else {
        config.params = cleanParams;
      }

      if (config.data) {
        config.data = convertData(config.data, 'camelCase-to-snake_case');
      }

      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => this.handleResponse(response),
      (error) => this.handleError(error),
    );
  };

  private static handleResponse = <T extends Record<string, unknown>>(
    response: AxiosResponse<T>,
  ): T & { statusCode?: number } => {
    const activeWait = response.status === 202;
    const result = convertData(response.data, 'snake_case-to-camelCase') as T;

    return activeWait ? { ...result, statusCode: response.status } : result;
  };

  private static handleError = (
    error: AxiosError<TAstroPayHttpError>,
  ): Promise<TAstroPayHttpError> => {
    const normalizedError = convertData(
      error.response?.data,
      'snake_case-to-camelCase',
    ) as TAstroPayHttpError;

    const errorResponse: TAstroPayHttpError = {
      error: normalizedError?.error || 'UNKNOWN_ERROR',
      internalTerm: normalizedError?.internalTerm,
      message: normalizedError?.message || AstroPayI18n.translate('generic_error'),
      description: normalizedError?.description,
    };

    return Promise.reject(errorResponse);
  };

  // #endregion
}
